import React, { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import customCss from '@/app/components/hotel-detail/css/customCss';
import { EVO_STICKY_TOP_PX } from '@/app/components/hotel-detail/common/consts';
import { Text } from '@lmn/design-system';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider';
import { useOffersContext } from '@/app/components/hotel-detail/OffersProvider';
import { useCommonClient } from '@/app/common/commonClientProvider';
import { useTranslation } from '@/app/components/translations/Translation';
import { useUrlSearchParams } from '@/app/components/hotel-detail/UrlSearchParamsProvider';
import { useInitialParams } from '@/app/components/hotel-detail/InitialParamsProvider';
import { useHotelStayDates } from '@/app/components/hotel-detail/hooks/useHotelStayDates';
import usePriceDisplay from '@/app/components/hotel-detail/hooks/usePriceDisplay.ts';
import { Icon } from '@/app/common/icons/Icon';
import { ApexPriceCalendar } from './ApexPriceCalendar';
import { TravelTimesSelector } from './TravelTimesSelector';
import { ApexCalendarCollapsibleFilters } from './ApexCalendarCollapsibleFilters';
import { SelectorHeader, SelectorLabel, SelectorValue } from './apexCalendarSelectors.styles';
import { HotelSummarySection } from './HotelSummarySection';
import { TransportSummarySection } from './TransportSummarySection';
import { MobileTransportSummary } from './MobileTransportSummary';
import { PriceAndBookSection } from './PriceAndBookSection';
import { TripOverviewPriceToggle } from './TripOverviewPriceToggle';
import { SummaryRow, SummaryRows } from './summarySection.styles';
import { formatOccupancy } from '../../package-review/PackageReviewSubheader';

// Sticky card sizing: the card is clamped each frame to the space between its
// current top and the viewport bottom so the price/CTA footer is always fully
// visible, and its content is not user-scrollable — the page scroll drives it
// (see the effect in EvoTripOverview). BOTTOM_MARGIN is the breathing room kept
// below the card; the card pins EVO_STICKY_TOP_PX from the viewport top, the gap
// the scroll to the rooms shares so the rooms title lines up with the card.
const BOTTOM_MARGIN = 16;

const OverviewSection = styled.div`
  height: 100%;
`;

const OverviewSticky = styled.div<{ $stickyTop: number }>`
  position: sticky;
  top: ${(p) => p.$stickyTop}px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const OverviewCard = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  background: white;
  border-radius: 16px;
  border: 1px solid ${({ theme }) => theme.kallisto.color.border.static.borderNeutral};
  box-shadow: ${({ theme }) => theme.kallisto.shadow.mediumDownShadow};
  // The 24px horizontal inset lives on OverviewContent / the footer band, not
  // here, so a section divider can break out to the full card width (FullBleedDivider).
  padding: 0;
  overflow: hidden;
  max-height: var(--card-height, none);
`;

// overflow-y hidden when the page scroll drives the card: the card reveals
// hidden content by extending upward, never by scrolling — this keeps the
// hidden part clipped above the footer and measurable via scrollHeight. In the
// mobile modal it stays user-scrollable (auto). Spacing toward the card edges
// is padding, not margin: margin sits outside the clip box and would cut the
// content off before it reaches the edges; padding belongs to the content, so
// it (title included) runs right up to the card top and the footer, and the
// breathing room only shows at either end. The 24px horizontal padding lives
// here too (not on the card) so a FullBleedDivider can use margin: 0 -24px to
// reach the card edges while staying flush inside this box — no horizontal
// overflow, so overflow-y: hidden never forces a stray x-scrollbar.
const OverviewContent = styled.div<{ $scrollable: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px 24px 12px;
  overflow-y: ${({ $scrollable }) => ($scrollable ? 'auto' : 'hidden')};
  flex: 1;
  min-height: 0;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const Divider = styled.div`
  height: 1px;
  // Never let the sticky card's overflow squeeze the 1px line to nothing: inside
  // OverviewContent (a height-bounded flex column whose content overflows), the
  // non-shrinkable sections push all the negative free space onto shrinkable
  // children, which would otherwise crush the dividers to 0px and hide them.
  flex-shrink: 0;
  background: ${(p) => p.theme.commons.colors.gray100};
  margin: 0;
`;

// The separator between the calendar block and the trip details spans the full
// card width, unlike the inset content dividers. It breaks out of OverviewContent's
// 24px horizontal padding; staying flush with the padding box keeps it within the
// card and avoids any horizontal overflow.
const FullBleedDivider = styled(Divider)`
  margin: 0 -24px;
`;

const InputsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

// Footer band for the pinned price/CTA. The separation from the content above
// (upward shadow vs plain divider) is decided and applied at the call site.
const PriceAndBookSectionWrapper = styled.div`
  padding: 16px 24px;
  border-radius: 0 0 16px 16px;
`;

const ApexCalendarFiltersContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.kallisto.space.space100};
`;

// Inline (card-less) container for the mobile trip overview, placed between the
// hero and the rooms. The desktop sidebar uses EvoTripOverview's sticky card.
const MobileContainer = styled.div`
  grid-area: trip-overview-mobile;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 0 16px 16px;

  @media ${customCss.breakpoints.sm} {
    padding: 0 24px;
  }

  @media ${customCss.breakpoints.lg} {
    padding: 24px;
  }
`;

/**
 * Desktop trip overview sidebar card. It reacts to the page scroll — it never
 * has a scrollbar of its own.
 *
 * When the whole card fits on screen, it simply stays in view while the user
 * scrolls the page, keeping its natural size.
 *
 * When the card is taller than the screen:
 * - The price and the Continue button are always visible, anchored at the
 *   bottom edge of the screen, no matter where the user is on the page.
 * - At the top of the page, the card shows its content from the beginning.
 *   Whatever doesn't fit is hidden below, behind the price bar.
 * - As the user scrolls down, the card first moves up together with the page.
 *   Once its top reaches the top of the screen, it keeps sliding up and out of
 *   view — progressively revealing the rest of its content at the bottom, just
 *   above the price bar. When everything has been revealed, the card stops and
 *   the rest of the page continues scrolling normally.
 * - As the user scrolls up, the same happens in reverse: the card slides back
 *   down, bringing the earlier content back into view, with the price bar
 *   staying in place. By the time the user is back at the top of the page, the
 *   card is back at its starting position.
 * - Near the end of the page, the card moves up naturally with the last
 *   section so it never overlaps the page footer. Scrolling up from there, it
 *   first settles back to the bottom of the screen before its content starts
 *   moving again.
 */
export function EvoTripOverview(
  props: {
    showChangeTransportButton?: boolean;
    showCalendar?: boolean;
    onModifyHotelClick?: () => void;
    showTransportModifyLink?: boolean;
    /** False where the window is not the scroller (the mobile modal): the card keeps its natural height and the modal body scrolls it. */
    pageScrollDriven?: boolean;
  } & React.HTMLAttributes<HTMLDivElement>
) {
  const {
    showChangeTransportButton = false,
    showCalendar: showCalendarProp = true,
    onModifyHotelClick,
    showTransportModifyLink = true,
    pageScrollDriven = true,
    ...rest
  } = props;
  const { t } = useTranslation();
  const { localization } = usePageWrapperData();
  // The Apex price calendar shows whenever apex is enabled. Its interactive
  // filters (date/traveller selector, collapsible filters, travel times) are
  // gated behind apexCalendarFiltersEnabled (off by default); without them the
  // calendar shows under static date/occupancy fields (TripStaticSection).
  const showCalendar = showCalendarProp && !!localization?.configValuesApp?.apexEnabled;
  const showCalendarFilters = showCalendar && !!localization?.configValuesApp?.apexCalendarFiltersEnabled;
  // The apex calendar carries the toggle under itself; this panel only shows one when it isn't there.
  const { canShowTotalPrice } = usePriceDisplay();
  const { isSoldout } = useOffersContext();
  const { accommodationOnly } = useUrlSearchParams();
  const stickyRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  // The evo header scrolls away with the page (it is not fixed), so the card
  // pins at the grid gap from the viewport top — no header height to clear.
  const stickyTop = EVO_STICKY_TOP_PX;

  // Each frame the card's max-height is clamped to the space between its
  // current top and the viewport bottom so the price/CTA footer is always
  // visible. When pageScrollDriven, the card never scrolls internally: hidden
  // content is revealed by extending the card upward — once the card has
  // pinned at stickyTop, scrolling down pushes its top (rounded corners
  // included) beyond the viewport while its bottom stays put, until all
  // content is shown; scrolling up retracts it as soon as its bottom sits at
  // the viewport bottom. The extension can never be ahead of the page offset,
  // so the card is fully retracted when the page is back at the top. Reading
  // the sticky element (not the card) avoids feedback loops, since its
  // position is independent of the card's height.
  useEffect(() => {
    const stickyEl = stickyRef.current;
    const card = cardRef.current;
    const content = contentRef.current;
    const column = stickyEl?.parentElement;
    if (!stickyEl || !card || !content || !column) return () => {};

    let frame = 0;
    let lastScrollY = window.scrollY;
    let lastTop: number | null = null;
    let lastHeight = -1;
    // How far the card's top is pushed beyond stickyTop — negative while the
    // card is still travelling below its pin in the upper part of the page.
    // Once scrolling starts, the card is ALWAYS held at stickyTop - extension:
    // position and height derive from this one value in the same frame, so the
    // bottom edge cannot wobble. (Letting CSS move the card through the upper
    // stretch while JS chased it with height updates is what made it vibrate:
    // the compositor-moved top and the lagging height disagreed every frame.)
    // Captured lazily on the first real scroll, so layout shifts while the
    // page settles can't desync it.
    let extension: number | null = null;

    const update = () => {
      frame = 0;
      const rect = stickyEl.getBoundingClientRect();
      const y = window.scrollY;
      const delta = y - lastScrollY;
      lastScrollY = y;
      // Whether the card sat at (or, near the column end, was carried past)
      // its pin before this frame's pin move.
      const engaged = extension !== null && rect.top <= stickyTop - extension + 1;

      if (pageScrollDriven) {
        if (extension === null) {
          // Plain CSS sticky until the first real scroll.
          if (delta !== 0) extension = stickyTop - rect.top;
        } else if (delta > 0) {
          // Move the pin to wherever travelling 1:1 with the page puts the
          // card this frame, capped at the page delta and at the budget left:
          // the remaining ride up to stickyTop plus the hidden content still
          // to reveal (measured fresh so content size changes self-correct).
          const target = stickyTop - (rect.top - delta);
          if (target > extension) {
            const room = content.scrollHeight - content.clientHeight;
            extension += Math.min(delta, target - extension, Math.max(0, -extension) + room);
          }
        } else if (delta < 0 && rect.top >= stickyTop - extension - 1) {
          // Retract 1:1, but not past the resting pin: stickyTop while deep in
          // the page (the card holds there once fully rewound), then the
          // column top once it comes down into view near the top of the page,
          // so the card rides back to its natural position under JS control
          // for the whole final stretch. Skipped while the column end carries
          // the card above its pin: scrolling up must first bring it back down
          // with the page before the content starts rewinding.
          const floor = Math.min(0, stickyTop - column.getBoundingClientRect().top);
          extension = Math.max(extension + delta, floor);
        }
        if (extension !== null) {
          const nextTop = stickyTop - extension;
          if (nextTop !== lastTop) {
            lastTop = nextTop;
            stickyEl.style.top = `${nextTop}px`;
          }
        }
      }

      // Position and height must describe the same box in the same frame.
      const pinTop = extension === null ? stickyTop : stickyTop - extension;
      const boxTop = engaged || rect.top <= pinTop + 1 ? pinTop : Math.max(pinTop, rect.top);
      const nextHeight = Math.max(0, window.innerHeight - boxTop - BOTTOM_MARGIN);
      if (nextHeight !== lastHeight) {
        lastHeight = nextHeight;
        card.style.setProperty('--card-height', `${nextHeight}px`);
      }
    };
    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [stickyTop, pageScrollDriven]);

  // The footer shows an upward shadow when there is hidden content sliding
  // under it, and a flat divider when the card content fits with nothing below.
  // Measure the content overflow directly rather than guessing from the step:
  // the ResizeObserver fires both when the card height changes as the page
  // scrolls and when the content itself grows or shrinks, so the treatment
  // always matches what is actually on screen. Initialised to the showCalendar
  // heuristic so the first paint (and SSR) is sensible before the observer runs.
  const [hasHiddenContent, setHasHiddenContent] = useState(showCalendar);
  useEffect(() => {
    const content = contentRef.current;
    if (!content || typeof ResizeObserver === 'undefined') return () => {};
    const measure = () => setHasHiddenContent(content.scrollHeight - content.clientHeight > 1);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(content);
    return () => observer.disconnect();
  }, []);

  const { starTrack: _starTrack } = useCommonClient();
  const initialParams = useInitialParams();
  const { productDetailKey: _productDetailKey } = initialParams;

  return (
    <OverviewSection {...rest}>
      <OverviewSticky ref={stickyRef} $stickyTop={stickyTop}>
        <OverviewCard ref={cardRef}>
          <OverviewContent ref={contentRef} $scrollable={!pageScrollDriven}>
            <Text variant="Title2">{t('vacation.select.cc.trip_overview.title')}</Text>
            {showCalendar && <ApexCalendarFilters showFilters={showCalendarFilters} />}
            {/* Dates + occupancy always show when the calendar isn't rendered — including
                when the offer is sold out, so the card never collapses to just its title
                (the calendar path renders its own TripStaticSection). */}
            {!showCalendar && <TripStaticSection />}
            {!isSoldout && (
              <>
                <FullBleedDivider />
                <HotelSummarySection onModifyClick={onModifyHotelClick} />
                {!accommodationOnly && (
                  <>
                    <Divider />
                    <TransportSummarySection showModifyLink={showTransportModifyLink} />
                  </>
                )}
                {!showCalendar && canShowTotalPrice && (
                  <>
                    <Divider />
                    <TripOverviewPriceToggle />
                  </>
                )}
              </>
            )}
          </OverviewContent>
          {/* The upward shadow signals that hidden content slides under the
              pinned price/CTA; when everything fits, the shadow reads as a
              detached floating card, so a flat divider separates instead. */}
          {!isSoldout && (
            <>
              {!hasHiddenContent && <Divider css="margin: 0 24px;" />}
              <PriceAndBookSectionWrapper
                css={
                  hasHiddenContent
                    ? css`
                        box-shadow: ${({ theme }) => theme.kallisto.shadow.mediumTopShadow};
                      `
                    : undefined
                }
              >
                <PriceAndBookSection showChangeTransportButton={showChangeTransportButton} />
              </PriceAndBookSectionWrapper>
            </>
          )}
        </OverviewCard>
      </OverviewSticky>
    </OverviewSection>
  );
}

// The Apex price calendar. The interactive filters above it (date/traveller
// selector, collapsible filters, travel-time selector) render only when
// showFilters is true (the apexCalendarFiltersEnabled toggle, off by default);
// otherwise the static date/occupancy fields (TripStaticSection) take their
// place. The price calendar itself always shows. Owns the collapsible-filters
// expand state so it can be dropped in anywhere.
function ApexCalendarFilters({ showFilters }: { showFilters: boolean }) {
  const [isExpanded, setIsExpanded] = useState(false);
  return (
    <ApexCalendarFiltersContainer>
      {showFilters ? (
        <>
          <DatesAndTravellersSection onToggleExpand={() => setIsExpanded((v) => !v)} />
          <ApexCalendarCollapsibleFilters isExpanded={isExpanded} />
          <TravelTimesSelector />
        </>
      ) : (
        <TripStaticSection />
      )}
      <ApexPriceCalendar />
    </ApexCalendarFiltersContainer>
  );
}

/**
 * Inline, card-less mobile trip overview: only the calendar with filters and the
 * flight info — no title, hotel summary, or price/book footer. Sits in the
 * `trip-overview-mobile` grid area between the hero and the rooms; drop it
 * directly into EvoDetailsLayout's grid.
 */
export function EvoTripOverviewMobile(props: React.HTMLAttributes<HTMLDivElement>) {
  const { t } = useTranslation();
  const { localization } = usePageWrapperData();
  const { accommodationOnly } = useUrlSearchParams();
  const { isSoldout } = useOffersContext();
  const showCalendar = !!localization?.configValuesApp?.apexEnabled;
  const showCalendarFilters = showCalendar && !!localization?.configValuesApp?.apexCalendarFiltersEnabled;

  return (
    <MobileContainer {...props}>
      <Text variant="Title4">{t('vacation.select.cc.trip_overview.title')}</Text>
      {showCalendar ? <ApexCalendarFilters showFilters={showCalendarFilters} /> : <TripStaticSection />}
      {!isSoldout && !accommodationOnly && (
        <>
          <Divider />
          <MobileTransportSummary />
        </>
      )}
    </MobileContainer>
  );
}

function DatesAndTravellersSection({ onToggleExpand }: { onToggleExpand: () => void }) {
  const { t } = useTranslation();
  const { adults, children, infants, totalRooms } = useUrlSearchParams();

  const travelerLabelKey =
    adults + children === 1
      ? 'vacation.select.details.tripoverview.traveller'
      : 'vacation.select.details.tripoverview.travellers';

  const roomsLabelKey =
    totalRooms === 1 ? 'vacation.select.details.tripoverview.room' : 'vacation.select.details.tripoverview.rooms';

  return (
    <InputsContainer>
      <SelectorHeader
        onClick={onToggleExpand}
        css={`
          display: flex;
          flex-direction: row;
          align-items: center;
          justify-content: space-between;
        `}
      >
        <div>
          <SelectorLabel>{t('vacation.select.cc.trip_overview.travellers_rooms')}</SelectorLabel>
          <SelectorValue>
            {t(travelerLabelKey, { travellers: (adults + children + infants).toString() })}
            {' · '}
            {t(roomsLabelKey, { rooms: totalRooms.toString() })}
          </SelectorValue>
        </div>
        <Icon category="Arrows" name="icon-unfold" size={20} color="iconPrimary" alt="" />
      </SelectorHeader>
    </InputsContainer>
  );
}

function parseYyyymmdd(dateStr: string): Date {
  return new Date(
    parseInt(dateStr.slice(0, 4), 10),
    parseInt(dateStr.slice(4, 6), 10) - 1,
    parseInt(dateStr.slice(6, 8), 10)
  );
}

function TripStaticSection() {
  const { localization } = usePageWrapperData();
  const { t } = useTranslation();
  const { locale } = localization;
  const { adults, children, infants } = useUrlSearchParams();
  // On a DP partial stay these are the hotel stay dates, not the URL (flight) dates.
  const { checkin, checkout, nights } = useHotelStayDates();

  const { adults: adultsLabel, children: childrenLabel } = formatOccupancy({ adults, children: children + infants }, t);

  const fmt = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' });
  const nightsLabel =
    nights === 1
      ? t('vacation.select.cc.trip_overview.nights.single')
      : t('vacation.select.cc.trip_overview.nights.plural', { count: nights.toString() });
  const dateLabel = `${fmt.format(parseYyyymmdd(checkin))} - ${fmt.format(parseYyyymmdd(checkout))} (${nightsLabel})`;

  // Infants are folded into the children count (matching PackageReviewSubheader)
  // rather than shown separately. childrenLabel is '' when there are no minors.
  const guestParts = [adultsLabel, childrenLabel].filter(Boolean);

  return (
    <SummaryRows
      css={css`
        padding-bottom: ${({ theme }) => theme.kallisto.space.space150};
      `}
    >
      <SummaryRow>
        <Icon category="Office and stationery" name="icon-calendar-grid" size={20} color="iconPrimary" alt="" />
        <Text variant="Body">{dateLabel}</Text>
      </SummaryRow>
      <SummaryRow>
        <Icon category="People and users" name="icon-multiple-users" size={20} color="iconPrimary" alt="" />
        <Text variant="Body">{guestParts.join(', ')}</Text>
      </SummaryRow>
    </SummaryRows>
  );
}
